import React, { useState, useRef } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import './AntiSpamCaptcha.css';
import { API_URL } from '../config/api';

const AntiSpamCaptcha = ({ onVerified, onCancel, businessId, initialLives = 3 }) => {
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [captchaLives, setCaptchaLives] = useState(initialLives);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockMessage, setBlockMessage] = useState('');
  const recaptchaRef = useRef(null);
  
  // Claves de reCAPTCHA (tu Site Key real)
  const RECAPTCHA_SITE_KEY = "6LdnZXsrAAAAAHe_Ue1G9hvu0TUaC2avYyQsQzIJ"; // Tu Site Key real

  // Manejar cambio en reCAPTCHA
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setError('');
    console.log('🔒 reCAPTCHA token recibido:', token ? 'Token válido' : 'Token nulo');
  };

  // Manejar expiración de reCAPTCHA
  const handleRecaptchaExpired = () => {
    setRecaptchaToken('');
    setError('El captcha ha expirado. Por favor, verifícalo nuevamente.');
    console.log('⏰ reCAPTCHA expirado');
  };

  // Reset reCAPTCHA
  const resetRecaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
      setRecaptchaToken('');
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      setError('Por favor completa la verificación reCAPTCHA');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/spam-protection/verify-recaptcha`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessId,
          recaptchaToken: recaptchaToken
        }),
      });

      const result = await response.json();

      if (result.valid) {
        // ✅ reCAPTCHA CORRECTO
        setCaptchaLives(result.captchaLives || 3);
        onVerified();
      } else if (result.blocked) {
        // 🚫 BLOQUEO POR VIDAS AGOTADAS
        setIsBlocked(true);
        setBlockMessage(result.error || 'Has agotado tus intentos');
        setError('');
        setCaptchaLives(0);
      } else {
        // ❌ reCAPTCHA INCORRECTO PERO AÚN QUEDAN VIDAS
        setCaptchaLives(result.captchaLives || 0);
        setError(result.message || 'Verificación fallida');
        
        // Reset reCAPTCHA solo si quedan vidas
        if (result.captchaLives > 0) {
          resetRecaptcha();
        }
      }
    } catch (error) {
      console.error('Error verificando reCAPTCHA:', error);
      setError('Error al verificar. Inténtalo nuevamente.');
      resetRecaptcha();
    } finally {
      setIsVerifying(false);
    }
  };



  return (
    <div className="captcha-overlay">
      <div className="captcha-modal">
        <div className="captcha-header">
          <h3>🔒 Verificación de Seguridad</h3>
          <p>Para continuar con tu segunda reserva, necesitamos verificar que eres una persona real.</p>
          
          {/* 🎮 SISTEMA DE VIDAS */}
          <div className="captcha-lives-display">
            <div className="lives-container">
              <span className="lives-label">Intentos restantes:</span>
              <div className="lives-hearts">
                {[...Array(3)].map((_, index) => (
                  <span 
                    key={index} 
                    className={`heart ${index < captchaLives ? 'alive' : 'dead'}`}
                  >
                    {index < captchaLives ? '❤️' : '💔'}
                  </span>
                ))}
              </div>
            </div>
            {captchaLives === 1 && (
              <div className="warning-message">
                ⚠️ <strong>¡Último intento!</strong> Ten cuidado al responder.
              </div>
            )}
          </div>
        </div>
        
        {/* 🚫 ESTADO DE BLOQUEO */}
        {isBlocked ? (
          <div className="captcha-blocked-state">
            <div className="blocked-icon">🚫</div>
            <h4>Acceso Temporalmente Bloqueado</h4>
            <p className="blocked-message">{blockMessage}</p>
            <div className="blocked-details">
              <p>💔 Has agotado tus 3 intentos para resolver el captcha</p>
              <p>🕐 Tu acceso a reservas está bloqueado por <strong>30 minutos</strong></p>
              <p>⏰ Después podrás intentar nuevamente</p>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="captcha-btn captcha-btn-cancel blocked-btn"
            >
              Entendido
            </button>
          </div>
        ) : (
          /* 📝 FORMULARIO DE reCAPTCHA */
          <form onSubmit={handleSubmit} className="captcha-form">
            <div className="captcha-question">
              <label>Completa la verificación de seguridad:</label>
              <p className="recaptcha-description">
                Demuestra que eres una persona real completando la verificación de Google reCAPTCHA
              </p>
            </div>
            
            <div className="recaptcha-container">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
                onExpired={handleRecaptchaExpired}
                theme="light"
                size="normal"
              />
              {error && <span className="captcha-error">{error}</span>}
            </div>
            
            <div className="captcha-buttons">
              <button
                type="button"
                onClick={onCancel}
                className="captcha-btn captcha-btn-cancel"
                disabled={isVerifying}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={resetRecaptcha}
                className="captcha-btn captcha-btn-refresh"
                disabled={isVerifying || captchaLives <= 0}
              >
                🔄 Nuevo reCAPTCHA
              </button>
              <button
                type="submit"
                className="captcha-btn captcha-btn-verify"
                disabled={isVerifying || !recaptchaToken || captchaLives <= 0}
              >
                {isVerifying ? '⏳ Verificando...' : '✓ Verificar'}
              </button>
            </div>
          </form>
        )}
        
        <div className="captcha-footer">
          <p>💡 <strong>¿Por qué veo esto?</strong></p>
          <p>Para proteger contra reservas automatizadas, verificamos usuarios después de su primera reserva.</p>
        </div>
      </div>
    </div>
  );
};

export default AntiSpamCaptcha; 